import {
  initialSettingsDoc,
  parseSettingsDoc,
  settingsDocFrom,
  type SettingsDoc,
} from '@il-colpo/shared/account';
import { SettingsSchema, type Settings } from '@il-colpo/shared/settings';
import { detectLanguage, useSettingsStore } from '../state/settingsStore.ts';

/**
 * What this device remembers about cross-save, in its own storage key: the settings as they stood
 * at the last change or sync with the time each setting last changed (the stamps that decide, key
 * by key, which device wins), and whose account the progress here was last synced with (so that
 * the progress of one player is never merged into the account of another on a shared device).
 *
 * Stamps are kept up to date at every change, signed in or not: a volume lowered last week offline
 * is a week-old change when the account is finally reached, not a change of today.
 */

export const SYNC_META_KEY = 'ilcolpo.sync';

export interface SyncMeta {
  settings: SettingsDoc | null;
  user: string | null;
}

export function loadSyncMeta(): SyncMeta {
  try {
    const raw = localStorage.getItem(SYNC_META_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { settings?: unknown; user?: unknown };
      return {
        settings: parseSettingsDoc(parsed.settings),
        user: typeof parsed.user === 'string' ? parsed.user : null,
      };
    }
  } catch {
    // Storage unavailable or damaged: as if nothing had been synced.
  }
  return { settings: null, user: null };
}

export function saveSyncMeta(meta: SyncMeta): void {
  try {
    localStorage.setItem(SYNC_META_KEY, JSON.stringify(meta));
  } catch {
    // Storage unavailable: the stamps last for this session only.
  }
}

/** What the settings are when nothing has been chosen. */
export const defaultSettingsHere = (): Settings =>
  SettingsSchema.parse({ language: detectLanguage() });

/** The settings document for the settings as they are now, stamping what changed since `meta`. */
export function currentSettingsDoc(settings: Settings, meta: SyncMeta, now: number): SettingsDoc {
  return meta.settings
    ? settingsDocFrom(settings, meta.settings, now)
    : initialSettingsDoc(settings, defaultSettingsHere(), now);
}

/**
 * Starts following the settings: every change updates the stamps on disk. Returns the function
 * that stops it. Called once at start-up, before any account is loaded.
 */
export function trackSettingsStamps(now: () => number = Date.now): () => void {
  const refresh = (settings: Settings): void => {
    const meta = loadSyncMeta();
    const doc = currentSettingsDoc(settings, meta, now());
    if (JSON.stringify(doc) !== JSON.stringify(meta.settings))
      saveSyncMeta({ ...meta, settings: doc });
  };
  refresh(useSettingsStore.getState().settings);
  return useSettingsStore.subscribe((state, previous) => {
    if (state.settings !== previous.settings) refresh(state.settings);
  });
}
